import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./api";
import { useWorkspaceScope } from "./workspace-queries";
import { Button } from "./components/ui/button";
import { at } from "./components/dashboard/format";
import type { MethodOverview } from "../../../packages/contracts/src/dashboard";
import type { Placement, ProposalEvidence } from "../../../packages/contracts/src/improvement";
import "./improvement.css";

/** Undo of a change Method made: the version before it becomes current again. */
export function UndoChange({ proposalId, variant = "secondary" }: { proposalId: string; variant?: "secondary" | "ghost" }) {
  const client = useQueryClient();
  const [state, setState] = useState<{ busy: boolean; error?: string }>({ busy: false });
  async function undo() {
    setState({ busy: true });
    try {
      await api.undoProposal(proposalId);
      // Undo changes the current version, so every page that shows the Method reads it again.
      await client.invalidateQueries({ predicate: query => query.queryKey[0] !== "session" });
      setState({ busy: false });
    } catch (error) {
      setState({ busy: false, error: error instanceof Error ? error.message : "The change was not undone." });
    }
  }
  return <>
    <Button type="button" variant={variant} disabled={state.busy} onClick={() => void undo()}>{state.busy ? "Undoing…" : "Undo"}</Button>
    {state.error && <span className="improvement-error" role="alert">{state.error}</span>}
  </>;
}

/** Next to Run on the Method page: how often its runs were judged right, and the last change Method made. */
export function ImprovementLine({ methodId, overview }: { methodId: string; overview: MethodOverview | null | undefined }) {
  if (!overview) return null;
  const { agreement, last_change: change } = overview;
  if (!agreement.judged && !change) return null;
  return <span className="improvement-slot" role="status">
    {agreement.judged > 0 && <span>Right in {agreement.right} of {agreement.judged} judged runs</span>}
    {change && <span>Method changed it {at(change.created_at)}:{" "}
      <Link to="/methods/$workflowId/proposals/$proposalId" params={{ workflowId: methodId, proposalId: change.id }}>{change.cause}</Link></span>}
    {change && change.status !== "rejected" && <UndoChange proposalId={change.id} variant="ghost" />}
  </span>;
}

type Verdict = "strong" | "weak";
/** Strong: every target case passes and the held-out runs (or the replay) win more than they lose. Weak: close or missing. */
function verdictOf(evidence: ProposalEvidence): Verdict {
  const targets = evidence.cases.filter(item => item.target).every(item => item.after === "pass");
  const score = evidence.judgments?.held_out.runs ? evidence.judgments.held_out : evidence.replay;
  return targets && score && score.wins > score.losses ? "strong" : "weak";
}
const verdictLabel: Record<Verdict, string> = { strong: "Strong", weak: "Weak" };
export function VerdictChip({ evidence }: { evidence: ProposalEvidence }) {
  const verdict = verdictOf(evidence);
  return <span className={`verdict is-${verdict}`}>{verdictLabel[verdict]}</span>;
}

const choices: Array<{ value: Placement; label: string }> = [
  { value: "workers", label: "Your workers" },
  { value: "cloud", label: "Method Cloud" },
];

/** Where the production runs of this Method run, per environment. */
export function PlacementSwitch({ methodId }: { methodId: string }) {
  const scope = useWorkspaceScope();
  const client = useQueryClient();
  const key = ["placements", scope, methodId];
  const placements = useQuery({ queryKey: key, queryFn: () => api.placements(methodId) });
  const [saving, setSaving] = useState<string>();
  const [error, setError] = useState<string>();
  async function change(environment: string, placement: Placement) {
    setSaving(environment);
    setError(undefined);
    try {
      await api.setPlacement(methodId, environment, placement);
      await client.invalidateQueries({ queryKey: key });
    } catch (e) {
      setError(e instanceof Error ? e.message : "The setting could not be saved.");
    } finally {
      setSaving(undefined);
    }
  }
  if (!placements.data) return placements.error ? <p className="placement-note">Could not load where this Method runs.</p> : null;
  const { cloud_enabled, placements: rows } = placements.data;
  return (
    <section className="method-placement" aria-label="Where it runs">
      <h2>Where it runs</h2>
      {rows.map(row => (
        <fieldset key={row.environment} className="placement-choice" disabled={saving === row.environment}>
          <legend>{rows.length > 1 || row.environment !== "production" ? row.environment : "Production runs"}</legend>
          {choices.map(choice => (
            <label key={choice.value}>
              <input
                type="radio"
                name={`placement-${row.environment}`}
                value={choice.value}
                checked={row.placement === choice.value}
                disabled={choice.value === "cloud" && !cloud_enabled}
                onChange={() => void change(row.environment, choice.value)}
              />
              {choice.label}
            </label>
          ))}
        </fieldset>
      ))}
      {!cloud_enabled && <p className="placement-note">Method Cloud is not turned on for your organization.</p>}
      {error && <p role="alert" className="placement-note">{error}</p>}
    </section>
  );
}
